import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { bibleLink } from '../bible/BiblePage';
import { useMe } from '../session/SessionProvider';
import { DayConversation } from './NotebookPage';

type Kind = 'familia' | 'amigos' | 'celula' | 'ministerio';
type Overview = {
  groups: { id: string; name: string; kind: Kind; planTitle: string; status: 'activo' | 'invitado'; today: number; totalDays: number }[];
  canStart: { family: boolean; cells: { id: string; name: string }[]; ministries: { id: string; name: string }[] };
};
const KIND: Record<Kind, string> = { familia: 'En familia', amigos: 'Con amigos', celula: 'Célula', ministerio: 'Ministerio' };

/** Mis grupos de lectura e invitaciones (en la lista de planes). */
export function MyReadingGroups() {
  const me = useMe();
  const base = `/devotional/churches/${me.activeChurchId}/reading-groups`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<Overview>(base, signal) : Promise.resolve(null)), [base]);
  const [error, setError] = useState<string | null>(null);
  if (data.status !== 'ready' || !data.data || data.data.groups.length === 0) return null;
  return (
    <Card title="Leer juntos">
      {error ? <Alert>{error}</Alert> : null}
      <ul className="list">
        {data.data.groups.map((g) => (
          <li key={g.id} className="list__item">
            <Link className="list__link" to={`/app/devocional/juntos/${g.id}`}>
              <strong>{g.name}</strong>
              <span className="small muted">{KIND[g.kind]} · {g.planTitle} · día {g.today} de {g.totalDays}</span>
            </Link>
            {g.status === 'invitado' ? (
              <Button onClick={() => api.post(`${base}/${g.id}/respond`, { action: 'aceptar' }).then(() => data.reload()).catch((e) => setError(errorMessage(e)))}>Aceptar</Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** En la página de un plan: empezar a leerlo en grupo. */
export function StartTogether({ planId }: { planId: string }) {
  const me = useMe();
  const base = `/devotional/churches/${me.activeChurchId}/reading-groups`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<Overview>(base, signal) : Promise.resolve(null)), [base]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('amigos');
  const [target, setTarget] = useState('');
  const [query, setQuery] = useState('');
  const [invite, setInvite] = useState<{ personId: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const found = useAsync(
    (signal) => (query.trim().length >= 2 ? api.get<{ items: { personId: string; name: string }[] }>(`${base}/people?q=${encodeURIComponent(query.trim())}`, signal).then((r) => r.items) : Promise.resolve([])),
    [base, query],
  );
  if (data.status !== 'ready' || !data.data) return null;
  const c = data.data.canStart;
  if (!open) return <div><Button variant="secondary" onClick={() => setOpen(true)}>Leer juntos</Button></div>;
  const kinds: Kind[] = [...(c.family ? (['familia'] as Kind[]) : []), 'amigos', ...(c.cells.length ? (['celula'] as Kind[]) : []), ...(c.ministries.length ? (['ministerio'] as Kind[]) : [])];

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>(base, { planId, kind, targetId: kind === 'celula' || kind === 'ministerio' ? target || null : null, invite: invite.map((p) => p.personId) });
      navigate(`/app/devocional/juntos/${id}`);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Card title="Leer juntos">
      <p className="muted small">Cada quien marca su lectura y todos ven quién ya leyó. Sin puntajes.</p>
      {error ? <Alert>{error}</Alert> : null}
      <fieldset className="choices choices--row">
        <legend>¿Con quién?</legend>
        {kinds.map((k) => (
          <label key={k} className="choice">
            <input type="radio" name="together-kind" checked={kind === k} onChange={() => { setKind(k); setTarget(''); }} />
            <span>{KIND[k]}</span>
          </label>
        ))}
      </fieldset>
      {kind === 'celula' || kind === 'ministerio' ? (
        <div className="field">
          <label htmlFor="together-target">{kind === 'celula' ? 'Célula' : 'Ministerio'}</label>
          <select id="together-target" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Elige…</option>
            {(kind === 'celula' ? c.cells : c.ministries).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
      ) : null}
      {kind === 'amigos' ? (
        <>
          <Field label="Invitar a" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Escribe un nombre" />
          <ul className="list">
            {(found.status === 'ready' ? found.data : []).filter((p) => !invite.some((i) => i.personId === p.personId)).map((p) => (
              <li key={p.personId} className="list__item">
                <span>{p.name}</span>
                <Button variant="ghost" onClick={() => { setInvite([...invite, p]); setQuery(''); }}>Invitar</Button>
              </li>
            ))}
          </ul>
          {invite.length ? <p className="small">Invitados: {invite.map((p) => p.name).join(', ')}</p> : null}
        </>
      ) : null}
      {kind === 'familia' ? <p className="small">Entra toda tu familia registrada en la iglesia.</p> : null}
      <div className="actions">
        <Button busy={busy} disabled={(kind === 'amigos' && invite.length === 0) || ((kind === 'celula' || kind === 'ministerio') && !target)} onClick={() => void start()}>Empezar juntos</Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
      </div>
    </Card>
  );
}

type Detail = {
  id: string; name: string; kind: Kind; planId: string; planTitle: string; today: number; totalDays: number; myStatus: 'activo' | 'invitado';
  days: { day: number; title: string | null; passages: string[] }[];
  members: { personId: string; name: string; status: string; completed: number[] }[];
};

/** Un grupo de lectura: la lectura de hoy y quién ya leyó cada día. */
export function ReadingGroupPage() {
  const { groupId = '' } = useParams();
  const me = useMe();
  const url = `/devotional/churches/${me.activeChurchId}/reading-groups/${groupId}`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<Detail>(url, signal) : Promise.resolve(null)), [url]);
  const [error, setError] = useState<string | null>(null);
  const [allDays, setAllDays] = useState(false);
  const navigate = useNavigate();
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} /></div>;
  const g = data.data;
  if (!g) return <EmptyState title="Elige primero una iglesia." />;
  const mine = g.members.find((m) => m.personId === me.personId);
  const toggle = (day: number, done: boolean) => api.put(`${url}/days/${day}`, { done }).then(() => data.reload()).catch((e) => setError(errorMessage(e)));
  const today = g.days.find((d) => d.day === g.today);

  return (
    <div className="page page--narrow">
      <p className="small"><Link to="/app/devocional">← Planes de lectura</Link></p>
      <header className="page__header">
        <h1>{g.name}</h1>
        <p className="muted">{KIND[g.kind]} · <Link to={`/app/devocional/${g.planId}`}>{g.planTitle}</Link> · día {g.today} de {g.totalDays}</p>
      </header>
      {error ? <Alert>{error}</Alert> : null}
      {g.myStatus === 'invitado' ? (
        <Alert tone="info">
          Te invitaron a leer juntos.{' '}
          <Button onClick={() => api.post(`${url}/respond`, { action: 'aceptar' }).then(() => data.reload())}>Aceptar</Button>
        </Alert>
      ) : null}
      {today ? (
        <Card title={`Hoy · día ${today.day}${today.title ? `: ${today.title}` : ''}`}>
          <span className="plan__passages">{today.passages.map((p) => <Link key={p} to={bibleLink(p)}>{p}</Link>)}</span>
          {g.myStatus === 'activo' ? (
            <label className="check">
              <input type="checkbox" checked={mine?.completed.includes(today.day) ?? false} onChange={(e) => void toggle(today.day, e.target.checked)} /> Ya lo leí
            </label>
          ) : null}
          <p className="small muted">
            Ya leyeron: {g.members.filter((m) => m.completed.includes(today.day)).map((m) => m.name).join(', ') || 'nadie todavía'}
          </p>
          {g.myStatus === 'activo' ? <DayConversation planId={g.planId} day={today.day} groupId={g.id} /> : null}
        </Card>
      ) : null}
      {/* En el celular: el avance de cada quien en lista; la tabla completa, detrás de «Ver todos los días». */}
      <Card title="Avance">
        <ul className="list reading-progress only-mobile">
          {g.members.map((m) => (
            <li key={m.personId} className="list__item">
              <span>
                <strong>{m.name}</strong>
                {m.status === 'invitado' ? ' (invitado)' : ''} · {m.completed.length} de {g.totalDays} días
              </span>
            </li>
          ))}
        </ul>
        <button type="button" className="btn btn--secondary only-mobile" aria-expanded={allDays} onClick={() => setAllDays((o) => !o)}>
          {allDays ? 'Ocultar los días' : 'Ver todos los días'}
        </button>
        <div className={`table-scroll reading-grid-wrap${allDays ? ' reading-grid-wrap--open' : ''}`}>
          <table className="reading-grid">
            <thead>
              <tr>
                <th>Día</th>
                {g.members.map((m) => <th key={m.personId}>{m.name.split(' ')[0]}{m.status === 'invitado' ? ' (invitado)' : ''}</th>)}
              </tr>
            </thead>
            <tbody>
              {g.days.map((d) => (
                <tr key={d.day} className={d.day === g.today ? 'reading-grid__today' : ''}>
                  <th scope="row" title={d.passages.join(', ')}>{d.day}</th>
                  {g.members.map((m) => (
                    <td key={m.personId}>
                      {m.personId === me.personId && g.myStatus === 'activo' ? (
                        <input type="checkbox" aria-label={`Día ${d.day} leído`} checked={m.completed.includes(d.day)} onChange={(e) => void toggle(d.day, e.target.checked)} />
                      ) : m.completed.includes(d.day) ? '✓' : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="actions">
        <Button variant="ghost" onClick={() => { if (window.confirm('¿Salir de este grupo?')) void api.post(`${url}/respond`, { action: 'salir' }).then(() => navigate('/app/devocional')); }}>Salir del grupo</Button>
      </div>
    </div>
  );
}
